
let refreshPromise: Promise<boolean> | null = null;

import { CacheManifest, CachedItem } from '@/types/cache';
import { smartCache } from './cache/SmartCacheManager';

/**
 * Intelligent fetch that prioritizes local cache but validates with server.
 * Layer 1: Load from IndexedDB immediately for fast UI.
 * Layer 2: Check version/checksum with server (background).
 * Layer 3: Refetch full content only if changed.
 */
export async function smartFetch<T>(id: string, type: 'save' | 'melody' | 'song', fetchUrl: string, checkUrl: string): Promise<CachedItem<T>> {
  // 1. Try to get from local cache first
  const cached = await smartCache.get<T>(id);
  
  // HOT CACHE OPTIMIZATION: If validated recently (< 300s), trust it and skip network check
  const HOT_CACHE_TTL = 300 * 1000; // 5 minutes
  if (cached && cached.manifest.lastValidatedAt && (Date.now() - cached.manifest.lastValidatedAt < HOT_CACHE_TTL)) {
    console.log(`SmartFetch: Using hot cache for ${id} (last validated ${Math.round((Date.now() - cached.manifest.lastValidatedAt)/1000)}s ago)`);
    return cached;
  }
  
  // Start background validation
  const validationPromise = (async (): Promise<CachedItem<T> | null> => {
    try {
      const checkRes = await apiFetch(`${checkUrl}?id=${id}`);
      if (!checkRes.ok) return null;
      
      const serverMeta = await checkRes.json();
      
      // If we have cache and it's valid, update lastValidatedAt and we are done
      if (cached && await smartCache.isValid(id, serverMeta.version, serverMeta.checksum)) {
        console.log(`SmartFetch: Cache hit and valid for ${id}`);
        
        // Update lastValidatedAt to prevent repeated checks within TTL
        const newManifest = { 
          ...cached.manifest, 
          lastValidatedAt: Date.now(),
          metadata: { ...cached.manifest.metadata, ...serverMeta } 
        };
        
        await smartCache.set(newManifest, cached.content);
        return { manifest: newManifest, content: cached.content };
      }

      // If missing or invalid, fetch fresh
      console.log(`SmartFetch: Cache miss or invalid for ${id}, fetching fresh...`);
      const fullRes = await apiFetch(fetchUrl);
      if (!fullRes.ok) throw new Error(`Failed to fetch fresh content for ${id}`);
      
      const freshContent = await fullRes.json();
      const contentData = freshContent.config || freshContent.data || freshContent;

      // Update cache
      const manifest = await smartCache.createManifest(
        id, 
        serverMeta.version || 1, 
        type, 
        contentData,
        serverMeta // The metadata we (now) get from check-version
      );
      await smartCache.set(manifest, contentData);
      
      return { manifest, content: contentData };
    } catch (e) {
      console.error(`SmartFetch: Error during background validation for ${id}`, e);
      return null;
    }
  })();

  // If we have cache, return it immediately while validation continues in background
  if (cached) {
    return cached;
  }

  // If no cache, wait for the full fetch
  const result = await validationPromise;
  if (!result) throw new Error(`Could not fetch data for ${id}`);
  return result;
}

/**
 * Global API utility for authenticated requests with automatic token refresh.
 * Now includes support for idempotency keys to prevent duplicate writes on retries.
 */
export async function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  // Generate or reuse idempotency key for write operations
  const isWrite = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method?.toUpperCase() || 'GET');
  
  // Safely extract existing idempotency key if present
  let existingKey: string | null = null;
  if (options.headers) {
    if (options.headers instanceof Headers) {
      existingKey = options.headers.get('X-Request-Id');
    } else if (Array.isArray(options.headers)) {
      const header = (options.headers as string[][]).find(h => h[0].toLowerCase() === 'x-request-id');
      if (header) existingKey = header[1];
    } else {
      existingKey = (options.headers as Record<string, string>)['X-Request-Id'];
    }
  }
  
  const idempotencyKey = existingKey || (isWrite ? crypto.randomUUID() : null);

  const finalOptions: RequestInit = {
    ...options,
    credentials: 'include',
    headers: {
      ...(options.headers instanceof Headers ? Object.fromEntries(options.headers.entries()) : 
          Array.isArray(options.headers) ? Object.fromEntries(options.headers) : 
          (options.headers as Record<string, string>) || {}),
      ...(idempotencyKey ? { 'X-Request-Id': idempotencyKey } : {}),
    },
  };

  let response = await fetch(url, finalOptions);

  // If unauthorized, try to refresh the token using a singleton promise to avoid race conditions
  if (response.status === 401 && !url.includes('/api/auth/refresh')) {
    console.log(`API: Unauthorized access to ${url}, attempting refresh...`);
    
    try {
      if (!refreshPromise) {
        refreshPromise = (async () => {
          try {
            const refreshRes = await fetch('/api/auth/refresh', { 
              method: 'POST', 
              credentials: 'include' 
            });
            return refreshRes.ok;
          } catch (e) {
            console.error('API: Global refresh error:', e);
            return false;
          } finally {
            refreshPromise = null;
          }
        })();
      }

      const refreshSuccessful = await refreshPromise;

      if (refreshSuccessful) {
        console.log(`API: Refresh successful, retrying original request to ${url}...`);
        // Retry with SAME idempotency key (already in finalOptions.headers)
        response = await fetch(url, finalOptions);
      } else {
         console.warn('API: Refresh failed, session might be invalid.');
      }
    } catch (e) {
      console.error('API: Refresh error during fetch retry:', e);
    }
  }

  return response;
}
